/**
 * Exit Detection Debug Panel
 *
 * Shows real-time logs from the visitor exit detection feature.
 * Only available in Electron development mode.
 */

import React, { useEffect, useState } from 'react';
import { X, Trash2, Terminal } from 'lucide-react';

export interface DebugLog {
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  data?: any;
}

export interface DeviceCheckStatus {
  deviceId: string | number;
  deviceName: string;
  lastCheckTime: Date;
  eventsFound: number;
  exitsDetected: number;
  cacheHit: boolean;
  error: string | null;
}

interface ExitDetectionDebugPanelProps {
  isOpen: boolean;
  onClose: () => void;
  logs: DebugLog[];
  onClearLogs: () => void;
  // Unified exit detection stats
  deviceStatuses?: Map<string | number, DeviceCheckStatus>;
  totalExitsDetected?: number;
  isChecking?: boolean;
  lastCheckTime?: Date | null;
  pollingInterval?: number;
  cacheStats?: {
    size: number;
    hits: number;
    misses: number;
    hitRate: number;
  };
  onClearCache?: () => void;
  onResetTimestamps?: () => void;
}

export const ExitDetectionDebugPanel: React.FC<ExitDetectionDebugPanelProps> = ({
  isOpen,
  onClose,
  logs,
  onClearLogs,
  deviceStatuses = new Map(),
  totalExitsDetected = 0,
  isChecking = false,
  lastCheckTime = null,
  pollingInterval = 60000,
  cacheStats = { size: 0, hits: 0, misses: 0, hitRate: 0 },
  onClearCache,
  onResetTimestamps,
}) => {
  const [autoScroll, setAutoScroll] = useState(true);
  const logsEndRef = React.useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  if (!isOpen) return null;

  const getLevelColor = (level: DebugLog['level']) => {
    switch (level) {
      case 'error':
        return 'text-red-400 bg-red-950/50';
      case 'warn':
        return 'text-yellow-400 bg-yellow-950/50';
      case 'info':
        return 'text-blue-400 bg-blue-950/50';
      case 'debug':
        return 'text-gray-400 bg-gray-950/50';
    }
  };

  const getLevelIcon = (level: DebugLog['level']) => {
    switch (level) {
      case 'error':
        return 'ERR';
      case 'warn':
        return 'WARN';
      case 'info':
        return 'INFO';
      case 'debug':
        return 'DBG';
    }
  };

  const deviceArray = Array.from(deviceStatuses.values());
  const successfulDevices = deviceArray.filter(d => !d.error);
  const failedDevices = deviceArray.filter(d => d.error);

  return (
    <div className="fixed bottom-4 right-4 w-[700px] max-h-[600px] bg-gray-950 border-2 border-blue-500/50 rounded-lg shadow-2xl z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-900 border-b border-gray-800">
        <div className="flex items-center gap-2">
          <Terminal className="h-4 w-4 text-blue-400" />
          <h3 className="text-sm font-semibold text-white">Detecção de Saída (DEV)</h3>
          <span className="text-xs text-gray-500">({logs.length} logs)</span>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
              className="rounded border-gray-700 bg-gray-900 text-blue-500 focus:ring-blue-500"
            />
            Auto-scroll
          </label>
          <button
            onClick={onClearLogs}
            className="p-1.5 hover:bg-gray-800 rounded transition-colors text-gray-400 hover:text-white"
            title="Clear logs"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-800 rounded transition-colors text-gray-400 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Status Bar */}
      <div className="px-4 py-2 bg-gray-900/50 border-b border-gray-800 text-xs space-y-2">
        {/* First row: Status and devices */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <span className="text-gray-500">Status:</span>
              <span className={`flex items-center gap-1 ${isChecking ? 'text-blue-400' : 'text-green-400'}`}>
                <span>{isChecking ? '' : ''}</span>
                <span className="font-medium">{isChecking ? 'Checking...' : 'Active'}</span>
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-gray-500">Interval:</span>
              <span className="text-white font-medium">{pollingInterval / 1000}s</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-gray-500">Last Check:</span>
              <span className="text-white font-medium">
                {lastCheckTime ? lastCheckTime.toLocaleTimeString('pt-BR') : '-'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-gray-500">Total Exits:</span>
            <span className="text-green-400 font-bold">{totalExitsDetected}</span>
          </div>
        </div>

        {/* Second row: Device summary */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <span className="text-gray-500">Devices:</span>
              <span className="text-green-400 font-medium">{successfulDevices.length} OK</span>
              {failedDevices.length > 0 && (
                <span className="text-red-400 font-medium">{failedDevices.length} Failed</span>
              )}
            </div>
          </div>
          {/* Cache stats */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <span className="text-gray-500">Cache:</span>
              <span className="text-white font-medium">{cacheStats.size} entries</span>
              <span className="text-gray-500">|</span>
              <span className="text-blue-400 font-medium">{(cacheStats.hitRate * 100).toFixed(0)}% hit rate</span>
            </div>
            <div className="flex items-center gap-2">
              {onClearCache && (
                <button
                  onClick={onClearCache}
                  className="px-2 py-0.5 bg-gray-800 hover:bg-gray-700 rounded text-[10px] text-gray-300"
                  title="Clear cache entries"
                >
                  Clear Cache
                </button>
              )}
              {onResetTimestamps && (
                <button
                  onClick={onResetTimestamps}
                  className="px-2 py-0.5 bg-yellow-900 hover:bg-yellow-800 rounded text-[10px] text-yellow-200"
                  title="Reset timestamps - next check will scan full history"
                >
                  Reset Timestamps
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Device status cards */}
        {deviceArray.length > 0 && (
          <div className="pt-2 border-t border-gray-800">
            <div className="grid grid-cols-2 gap-2">
              {deviceArray.map((device) => (
                <div
                  key={device.deviceId}
                  className={`p-2 rounded border ${
                    device.error
                      ? 'border-red-900 bg-red-950/30'
                      : 'border-green-900 bg-green-950/30'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-white text-[11px]">{device.deviceName}</span>
                    {device.cacheHit && (
                      <span className="text-[9px] px-1.5 py-0.5 bg-blue-900/50 text-blue-300 rounded">
                        CACHED
                      </span>
                    )}
                  </div>
                  {device.error ? (
                    <p className="text-red-400 text-[10px]">{device.error}</p>
                  ) : (
                    <div className="flex items-center gap-2 text-[10px]">
                      <span className="text-gray-400">
                        Total: <span className="text-white font-medium">{device.totalEvents || 0}</span>
                      </span>
                      <span className="text-gray-400">
                        Success: <span className="text-blue-400 font-medium">{device.eventsFound}</span>
                      </span>
                      <span className="text-gray-400">
                        Exits: <span className="text-green-400 font-medium">{device.exitsDetected}</span>
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Logs Container */}
      <div className="flex-1 overflow-y-auto p-3 space-y-1.5 bg-black/50 font-mono text-xs">
        {logs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Terminal className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No logs yet. Waiting for exit detection events...</p>
          </div>
        ) : (
          logs.map((log, index) => (
            <div
              key={index}
              className={`px-3 py-2 rounded border border-gray-800 ${getLevelColor(log.level)}`}
            >
              <div className="flex items-start gap-2">
                <span className="text-base flex-shrink-0">{getLevelIcon(log.level)}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-gray-500 text-[10px]">{log.timestamp}</span>
                  </div>
                  <p className="text-gray-200 break-words text-sm">{log.message}</p>
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={logsEndRef} />
      </div>

      {/* Footer */}
      <div className="px-4 py-2 bg-gray-900 border-t border-gray-800 text-[10px] text-gray-500 flex items-center justify-between">
        <span>Development Debug Panel</span>
        <span>Press Escape to close</span>
      </div>
    </div>
  );
};

// Global debug log store (singleton)
class DebugLogStore {
  private logs: DebugLog[] = [];
  private listeners: Set<(logs: DebugLog[]) => void> = new Set();
  private maxLogs = 100;

  addLog(level: DebugLog['level'], message: string, data?: any) {
    const log: DebugLog = {
      timestamp: new Date().toLocaleTimeString('pt-BR'),
      level,
      message,
      data,
    };

    this.logs.push(log);

    // Keep only last maxLogs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    this.notifyListeners();
  }

  getLogs(): DebugLog[] {
    return this.logs;
  }

  clearLogs() {
    this.logs = [];
    this.notifyListeners();
  }

  subscribe(listener: (logs: DebugLog[]) => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener([...this.logs]));
  }
}

export const debugLogStore = new DebugLogStore();

// Hook to access debug logs
export const useDebugLogs = () => {
  const [logs, setLogs] = useState<DebugLog[]>(debugLogStore.getLogs());

  useEffect(() => {
    const unsubscribe = debugLogStore.subscribe(setLogs);
    return unsubscribe;
  }, []);

  return {
    logs,
    clearLogs: () => debugLogStore.clearLogs(),
  };
};
